import axios, { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { useAppSelector } from '../app/hooks';
import { TDirectorInfo, TStep3Branch, TStep6Info } from '../types';
import { baseUrl_, timeout } from '../utils/constants';
import { decryptJson } from '../utils/decryptJson';
import { encryptEntrepreneurData } from '../utils/encryptJson';
import { handleError } from '../utils/errorHandler';
import { wqkgx } from '../config/keys';
import CryptoJS from 'crypto-js';
import customToast, { ToastType } from '../components_/Toast';
import { Request } from '../apis/request';

type TParams = {
  step1: any;
  step3Branches: TStep3Branch[];
  directors: TDirectorInfo[];
  shareholders: TDirectorInfo[];
  directorsInShareholder: TDirectorInfo[];
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useCreateLimitedLiabilityNew = ({
  directors,
  shareholders,
  step1,
  step3Branches,
  directorsInShareholder,
  setShowModal,
}: TParams) => {
  const [encryptedStr, setEncryptedStr] = useState('');
  const formData = new FormData();

  const errorFn = (error: AxiosError<{ message: string }>) => {
    if (error.response?.data.message) {
      customToast(error.response?.data.message, ToastType.error);
    } else {
      customToast('sorry, an error occured', ToastType.error);
    }
  };

  const getShareholderDocs: TStep6Info[] = useMemo(() => {
    const docs = shareholders?.map((shareholder) => {
      return {
        passport: shareholder?.passport,
        idCard: shareholder?.idCard,
        proofOfAddress: shareholder?.proofOfAddress,
        signature: shareholder?.signature,
      };
    });
    return docs;
  }, [shareholders]);

  const getDirectorDocs: TStep6Info[] = useMemo(() => {
    const docs = directors?.map((director) => {
      return {
        passport: director?.passport,
        idCard: director?.idCard,
        proofOfAddress: director?.proofOfAddress,
        signature: director?.signature,
      };
    });
    return docs;
  }, [directors]);

  const dirWithSharesIdentityNo = directorsInShareholder.map((director) => {
    return director.identityNumber;
  });
  const businessName = [
    step1?.firstbusinessName,
    step1?.secondbusinessName,
    step1?.threebusinessName,
    step1?.fourthbusinessName,
  ];
  const businessAddress = {
    hasBranch: step3Branches?.[0]?.hasBranch,
    nearestSterlingBranch: step3Branches?.[0]?.nearestSterlingBranch,
    nearestSterlingState: step3Branches?.[0]?.nearestSterlingState?.label,
    selectedLga: step3Branches?.[0]?.selectedLga,
    selectedState: step3Branches?.[0]?.selectedState,
    streetName: step3Branches?.[0]?.streetName,
    streetNumber: step3Branches?.[0]?.streetNumber,
  };
  const branches = step3Branches?.slice(1).map((branch) => {
    return {
      selectedLga: branch?.selectedLga,
      selectedState: branch?.selectedState,
      nearestSterlingBranch: branch?.nearestSterlingBranch,
      nearestSterlingState: branch?.nearestSterlingState,
      streetName: branch?.streetName,
      streetNumber: branch?.streetNumber,
    };
  });

  const directorsAddress = directors.map((director) => {
    return {
      cityOrTown: director?.cityOrTown,
      lga: director?.lga,
      state: director?.locatedState,
      streetName: director?.streetName,
      streetNumber: director?.streetNumber,
    };
  });
  const shareholdersAddress = shareholders.map((shareholder) => {
    return {
      cityOrTown: shareholder?.cityOrTown,
      lga: shareholder?.lga,
      state: shareholder?.locatedState,
      streetName: shareholder?.streetName,
      streetNumber: shareholder?.streetNumber,
    };
  });

  const directors_ = directors.map((director) => {
    return {
      lastName: director.lastName,
      isShareholder: false,
      gender: director.gender,
      identityNumber: director.identityNumber,
      occupation: director.occupation,
      address: [
        {
          cityOrTown: director?.cityOrTown,
          lga: director?.lga,
          state: director?.locatedState,
          streetName: director?.streetName,
          streetNumber: director?.streetNumber,
        },
      ],
      isDirector: true,
      phoneNumber: director.phone,
      identityType: 'Voters Card',
      dateOfBirth: director.birthDate,
      firstName: director.firstName,
      otherName: director.middleName,
      nationality: director.nationality,
      email: director.email,
    };
  });

  const getShares = (value: string | undefined) => {
    const valueInPercent = Number(value ?? 0) / 100;
    return valueInPercent * 1000000;
  };
  const shareholders_ = shareholders.map((shareholder) => {
    return {
      lastName: shareholder.lastName,
      isShareholder: true,
      gender: shareholder.gender,
      identityNumber: shareholder.identityNumber,
      occupation: shareholder.occupation,
      address: [
        {
          cityOrTown: shareholder?.cityOrTown,
          lga: shareholder?.lga,
          state: shareholder?.locatedState,
          streetName: shareholder?.streetName,
          streetNumber: shareholder?.streetNumber,
        },
      ],
      isDirector: dirWithSharesIdentityNo.includes(shareholder.identityNumber) ? true : false,
      phoneNumber: shareholder.phone,
      identityType: 'Voters Card',
      dateOfBirth: shareholder.birthDate,
      bvn: shareholder.bvn,
      firstName: shareholder.firstName,
      otherName: shareholder.middleName,
      nationality: shareholder.nationality,
      email: shareholder.email,
      numberOfShares: getShares(shareholder?.share),
    };
  });

  const packageId = decryptJson(localStorage.getItem('package-id'));

  const partners = [...directors_, ...shareholders_];
  const partnersDocs: any = [];

  for (const doc of [...getShareholderDocs, ...getDirectorDocs]) {
    for (const value of Object.values(doc)) {
      partnersDocs.push(value);
    }
  }

  const itemObj: any = {
    businessPackage: 'Limited Liability Company',
    packageId,
    businessSegment: step1?.businessSegment,
    businessNature: step1.businessNature,
    businessDescription: step1?.businessDesc,
    businessStructure: 'limited-liability-1-million-share',
    businessNameOptions: JSON.stringify(businessName),
    state: step3Branches?.[0]?.selectedState,
    lga: step3Branches?.[0]?.selectedLga,
    businessAddress: JSON.stringify(businessAddress),
    branchAddress: JSON.stringify(branches),
    totalNumberOfShares: '1000000',
    noOfPartners: directors.length + shareholders.length,
    partnerInfo: JSON.stringify(partners),
  };
  formData.append('data', encryptedStr);

  partnersDocs.forEach((doc: any) => {
    formData.append(`partnersDocs`, doc);
  });
  const token = localStorage.getItem('login-token');
  const createEntrepreneur = useMutation(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) =>
      Request.post(`/entrepreneur/limited-liability`, data, {
        headers: {
          Authorization: `Bearer ${token ?? ''}`,
          sessId: localStorage.getItem('sessId') ?? '',

          'Content-Type': 'multipart/form-data',
        },
        timeout: timeout,
      }),
    {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      onSuccess: async (data) => {
        const loginEmail = localStorage.getItem('login-email') ?? '';
        localStorage.removeItem(loginEmail + 'ltd');
        localStorage.removeItem('completed-steps');

        setShowModal(true);
        const res = decryptJson(data?.data?.data);
        localStorage.setItem(
          'businessId',
          CryptoJS.AES.encrypt(JSON.stringify(res.id), wqkgx).toString(),
        );
        localStorage.setItem('paymentToken', res.token);
      },
      onError: (error: AxiosError<{ message: string }>) => {
        handleError({ error, errorFn });
      },
    },
  );

  const callCreateEntrepreneurMutation = () => {
    setEncryptedStr(encryptEntrepreneurData(itemObj));
    if (encryptedStr) {
      createEntrepreneur.mutate(formData);
    }
  };

  return { callCreateEntrepreneurMutation, loading: createEntrepreneur.isLoading };
};
